import { isApiException, isAxiosApiProblemWithDetail } from "@vyne-shared/api/errors";
import { isMissingLegalInfoError, missingLegalInfoModal } from "api/exceptions";
import type { HookAPI } from "antd/es/modal/useModal";
import type { NiceModalHandler } from "@ebay/nice-modal-react";

/**
 * Display an error if data submission fails.
 * @param antdModal The AntD `modal` provided by the AntD App.
 * @param error The error that was encountered.
 * @param hostModal The handler for the modal dialog where data submission took place.
 */
export function displaySubmissionError(antdModal: HookAPI, error: unknown, hostModal: NiceModalHandler) {
    return (
        tryDisplayMissingLegalInfo(antdModal, error) ||
        tryDisplayPatientOverdetermined(antdModal, error) ||
        tryDisplayNotificationFailed(antdModal, error, hostModal) ||
        tryDisplayGenericDetail(antdModal, error) ||
        displayGenericFallback(antdModal)
    );
}

function tryDisplayMissingLegalInfo(antdModal: HookAPI, error: unknown) {
    if (isMissingLegalInfoError(error)) {
        antdModal.error(missingLegalInfoModal);
        return true;
    }
    return false;
}

function tryDisplayPatientOverdetermined(antdModal: HookAPI, error: unknown) {
    if (isApiException(error, 409, "PatientOverdeterminedException")) {
        antdModal.error({
            title: "Patient already exists",
            content:
                "There's a patient with some matching details already in the system. To make this payment request, please locate the existing patient first. " +
                error.response.data.detail,
        });
        return true;
    }
    return false;
}

function tryDisplayNotificationFailed(antdModal: HookAPI, error: unknown, hostModal: NiceModalHandler) {
    if (
        isApiException(error, 409, "NotificationFailedException") ||
        isApiException(error, 409, "OperaMissingException")
    ) {
        antdModal.warning({
            title: "Patient charged, but not notified",
            content:
                "The patient's balance has been updated, but we were unable to notify the patient: " +
                error.response.data.detail,
            afterClose() {
                // For this scenario, close the dialog when the error is dismissed because the form data is no longer up
                // to date, and the user should not be resubmitting anyway--the transaction happened!
                void hostModal.hide();
            },
        });
        return true;
    }
    return false;
}

function tryDisplayGenericDetail(antdModal: HookAPI, error: unknown) {
    if (isAxiosApiProblemWithDetail(error)) {
        antdModal.error({
            title: "Unable to submit new payment",
            content: error.response.data.detail,
        });
        return true;
    }
    return false;
}

function displayGenericFallback(antdModal: HookAPI) {
    antdModal.error({
        title: "Unable to submit new payment",
        content: "Please review the payment data you have entered. Contact support if this error persists.",
    });
    return true;
}
